<template>
  <div class="duplicate-orders">
    <div class="d-flex align-items-center justify-content-between mb-2">
      <div>
        <h2 class="mb-0 font-weight-bolder">
          Commandes en doublon
        </h2>
        <small class="text-muted">{{ duplicates.length }} commande(s) concernée(s)</small>
      </div>
    </div>

    <div v-if="loading" class="d-flex justify-content-center py-5">
      <b-spinner variant="primary" label="Chargement..." />
    </div>

    <b-card v-else-if="!duplicates.length" class="text-center py-4">
      <feather-icon icon="CheckCircleIcon" size="48" class="text-success mb-1" />
      <p class="mb-0 text-muted">
        Aucun doublon détecté.
      </p>
    </b-card>

    <b-card v-else no-body class="mb-0">
      <b-table
        :items="duplicates"
        :fields="tableColumns"
        responsive
        show-empty
        empty-text="Aucun doublon trouvé"
      >
        <template #cell(number)="data">
          <b-link
            :to="{ name: 'commandeDetail', params: { id: data.item.number } }"
            class="font-weight-bold"
          >
            {{ data.item.number }}
          </b-link>
        </template>

        <template #cell(status)="data">
          <feather-icon
            v-if="resolveStatusIcon(data.item.status)"
            :icon="resolveStatusIcon(data.item.status)"
            size="20"
            :class="`text-${resolveStatusIconColor(data.item.status)}`"
          />
          <b-badge pill variant="light-primary" class="text-capitalize">
            {{ (data.item.status) }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-spinner
            v-if="deletingId === data.item._id"
            small
            variant="danger"
          />
          <feather-icon
            v-else
            icon="Trash2Icon"
            size="16"
            class="text-danger cursor-pointer"
            @click="deleteOrder(data.item)"
          />
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BTable, BBadge, BSpinner, BLink, VBTooltip,
} from 'bootstrap-vue'

export default {
  name: 'DuplicateOrders',

  components: {
    BCard,
    BTable,
    BBadge,
    BSpinner,
    BLink,
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  data() {
    return {
      loading: false,
      deletingId: null,
      duplicates: [],
      tableColumns: [
        { key: 'number', label: 'N° commande', sortable: true },
        { key: 'status', label: 'Statut', sortable: true },
        { key: 'actions', label: '', sortable: false },
      ],
    }
  },

  mounted() {
    this.fetchDuplicates()
  },

  methods: {
    async fetchDuplicates() {
      try {
        this.loading = true
        const { duplicates } = await this.$http.get('/admin/orders/list-duplicate')
        this.duplicates = duplicates || []
      } catch (err) {
        console.error(err)
        this.$bvToast.toast('Impossible de charger les doublons.', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },

    async deleteOrder(order) {
      try {
        this.deletingId = order._id

        await this.$http.get(`/admin/orders/${order._id}/delete-order`)

        // Retire le doc supprimé
        this.duplicates = this.duplicates.filter(d => d._id !== order._id)

        // Retire les commandes qui n'ont plus de doublon (number unique restant)
        const counts = this.duplicates.reduce((acc, d) => {
          acc[d.number] = (acc[d.number] || 0) + 1
          return acc
        }, {})
        this.duplicates = this.duplicates.filter(d => counts[d.number] >= 2)
      } catch (err) {
        console.error(err)
        this.$bvToast.toast('Erreur lors de la suppression.', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.deletingId = null
      }
    },

    resolveStatusIconColor(status) {
      const map = {
        processing: 'success',
        pending: 'success',
        'on-hold': 'warning',
        completed: 'warning',
        failed: 'danger',
        cancelled: 'primary',
        refunded: 'primary',
      }
      return map[status] || false
    },

    resolveStatusIcon(status) {
      const map = {
        processing: 'ToolIcon',
        pending: 'ClockIcon',
        'on-hold': 'MinusCircleIcon',
        completed: 'CalendarIcon',
        failed: 'AlertCircleIcon',
        cancelled: 'XCircleIcon',
        refunded: 'RepeatIcon',
      }
      return map[status] || false
    },

    resolveStatusTranslation(status) {
      const map = {
        processing: 'En fabrication',
        pending: 'En attente de paiement',
        'on-hold': 'En attente',
        sended: 'Expédiée',
        completed: 'Expédiée',
        failed: 'Échouée',
        cancelled: 'Annulée',
        refunded: 'Remboursée',
        'rembourse-partiel': 'Remboursée partiellement',
      }
      return map[status] || status
    },
  },
}
</script>
